/**
 * Invented employees for the Meridian Associates mockup tenant.
 * Eight people; Sarah Chen is the admin, the rest are members.
 *
 * Two exports:
 *   - EMPLOYEES: canonical roster (id, name, role, businessUnitId, email)
 *   - EMPLOYEES_WITH_STATS: roster enriched with timesheet-derived aggregates
 *     for list/card views (Team Stats, Employee Cards, etc.)
 *
 * All figures are invented and pre-computed at module load, not derived from
 * live timesheet data — the fixture layer is presentational.
 */

import type { Employee } from '@/types/entities';
import { FROZEN_NOW_MS } from '@/lib/frozenTime';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface EmployeeWithStats extends Employee {
  totalHours: number;
  billableHours: number;
  generalHours: number;
  events: number;
  billablePercent: number;
  fullDays: number;
  halfDays: number;
  checkIns: number;
  effectiveDays: number;
  avgHoursDay: number;
  avgBillableDay: number;
}

export const EMPLOYEES = [
  {
    id: 'emp-sarah-chen',
    name: 'Sarah Chen',
    role: 'admin',
    businessUnitId: 'bu-corporate',
    email: 'schen@firm.com',
  },
  {
    id: 'emp-marcus-rivera',
    name: 'Marcus Rivera',
    role: 'member',
    businessUnitId: 'bu-litigation',
    email: 'mrivera@firm.com',
  },
  {
    id: 'emp-anita-patel',
    name: 'Aisha Patel',
    role: 'member',
    businessUnitId: 'bu-corporate',
    email: 'apatel@firm.com',
  },
  {
    id: 'emp-james-obrien',
    name: "James O'Brien",
    role: 'member',
    businessUnitId: 'bu-litigation',
    email: 'jobrien@firm.com',
  },
  {
    id: 'emp-wei-zhang',
    name: 'Wei Zhang',
    role: 'member',
    businessUnitId: 'bu-ip',
    email: 'wzhang@firm.com',
  },
  {
    id: 'emp-eva-kowalski',
    name: 'Elena Kowalski',
    role: 'member',
    businessUnitId: 'bu-corporate',
    email: 'ekowalski@firm.com',
  },
  {
    id: 'emp-daniel-kim',
    name: 'David Kim',
    role: 'member',
    businessUnitId: 'bu-tax',
    email: 'dkim@firm.com',
  },
  {
    id: 'emp-lisa-thompson',
    name: 'Lisa Thompson',
    role: 'member',
    businessUnitId: 'bu-tax',
    email: 'lthompson@firm.com',
  },
] as const satisfies readonly Employee[];

/**
 * Deactivated-at timestamps keyed by employee id — 104c surface.
 * Kept separate from the main roster so the Employee schema in
 * `schemas/employees.ts` stays stable. Only one row is populated so the
 * Show-deactivated toggle on the Employees page has something to reveal
 * in mockup mode.
 */
export const EMPLOYEES_DEACTIVATED_AT: Readonly<Record<string, string>> = {
  'emp-lisa-thompson': new Date(FROZEN_NOW_MS - 12 * DAY_MS).toISOString(),
};

export const EMPLOYEES_WITH_STATS: readonly EmployeeWithStats[] = [
  {
    ...EMPLOYEES[0],
    totalHours: 142.5,
    billableHours: 118.2,
    generalHours: 24.3,
    events: 1710,
    billablePercent: 83,
    fullDays: 16,
    halfDays: 5,
    checkIns: 1,
    effectiveDays: 18.5,
    avgHoursDay: 7.5,
    avgBillableDay: 6.2,
  },
  {
    ...EMPLOYEES[1],
    totalHours: 128.0,
    billableHours: 102.4,
    generalHours: 25.6,
    events: 1536,
    billablePercent: 80,
    fullDays: 15,
    halfDays: 4,
    checkIns: 3,
    effectiveDays: 17.0,
    avgHoursDay: 7.1,
    avgBillableDay: 5.7,
  },
  {
    ...EMPLOYEES[2],
    totalHours: 119.8,
    billableHours: 95.8,
    generalHours: 24.0,
    events: 1438,
    billablePercent: 80,
    fullDays: 14,
    halfDays: 5,
    checkIns: 2,
    effectiveDays: 16.5,
    avgHoursDay: 6.9,
    avgBillableDay: 5.5,
  },
  {
    ...EMPLOYEES[3],
    totalHours: 112.3,
    billableHours: 78.6,
    generalHours: 33.7,
    events: 1348,
    billablePercent: 70,
    fullDays: 13,
    halfDays: 4,
    checkIns: 4,
    effectiveDays: 15.0,
    avgHoursDay: 6.6,
    avgBillableDay: 4.6,
  },
  {
    ...EMPLOYEES[4],
    totalHours: 108.5,
    billableHours: 86.8,
    generalHours: 21.7,
    events: 1302,
    billablePercent: 80,
    fullDays: 13,
    halfDays: 3,
    checkIns: 3,
    effectiveDays: 14.5,
    avgHoursDay: 6.8,
    avgBillableDay: 5.4,
  },
  {
    ...EMPLOYEES[5],
    totalHours: 98.2,
    billableHours: 68.7,
    generalHours: 29.5,
    events: 1178,
    billablePercent: 70,
    fullDays: 11,
    halfDays: 5,
    checkIns: 2,
    effectiveDays: 13.5,
    avgHoursDay: 6.1,
    avgBillableDay: 4.3,
  },
  {
    ...EMPLOYEES[6],
    totalHours: 75.0,
    billableHours: 37.5,
    generalHours: 37.5,
    events: 900,
    billablePercent: 50,
    fullDays: 8,
    halfDays: 4,
    checkIns: 6,
    effectiveDays: 10.0,
    avgHoursDay: 5.0,
    avgBillableDay: 2.5,
  },
  {
    ...EMPLOYEES[7],
    totalHours: 63.2,
    billableHours: 24.0,
    generalHours: 39.2,
    events: 758,
    billablePercent: 38,
    fullDays: 6,
    halfDays: 5,
    checkIns: 4,
    effectiveDays: 8.5,
    avgHoursDay: 4.2,
    avgBillableDay: 1.6,
  },
];
